import { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';

import noComparisonDataPng from '@/assets/illustrations/empty/no-comparison-data.png';

import { useComparisonStore } from '@/stores/comparison-store';
import { usePropertyStore } from '@/stores/property-store';
import { useWaypointStore } from '@/stores/waypoint-store';
import { useLogEntryStore } from '@/stores/log-entry-store';
import { useUserProfileStore } from '@/stores/user-profile-store';
import { UpsellSheet } from '@/components/common/UpsellSheet';
import { UPSELL_COPY } from '@/constants/upsell-copy';
import { OperationalGuide } from '@/components/common/OperationalGuide';
import { PeriodPicker } from '@/components/comparison/PeriodPicker';
import { ComparisonSummary } from '@/components/comparison/ComparisonSummary';
import { ComparisonTimeline } from '@/components/comparison/ComparisonTimeline';
import { hasMultiYearScoutingData } from '@/utils/comparison-utils';
import type { DateRange } from '@/types/comparison';

// ---------------------------------------------------------------------------
// Waypoint-level comparison
// ---------------------------------------------------------------------------

export function WaypointComparisonPage() {
  const { propertyId, waypointId } = useParams<{
    propertyId: string;
    waypointId: string;
  }>();
  const navigate = useNavigate();

  const { properties, loadProperties } = usePropertyStore();
  const { waypoints, loadWaypoints } = useWaypointStore();
  const { entries, loadEntriesByWaypoint } = useLogEntryStore();
  const { result, isLoading, error, setPeriodA, setPeriodB, loadComparison, reset } =
    useComparisonStore();
  const isPro = useUserProfileStore((s) => s.isPro);

  useEffect(() => {
    if (properties.length === 0) void loadProperties();
  }, [properties.length, loadProperties]);

  useEffect(() => {
    if (propertyId && waypoints.length === 0) void loadWaypoints(propertyId);
  }, [propertyId, waypoints.length, loadWaypoints]);

  useEffect(() => {
    if (waypointId) void loadEntriesByWaypoint(waypointId);
  }, [waypointId, loadEntriesByWaypoint]);

  // Reset comparison state when leaving the page
  useEffect(() => {
    return () => {
      reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const waypoint = waypoints.find((w) => w.id === waypointId);

  if (!propertyId || !waypointId) {
    return <p className="text-body-sm text-[var(--color-text-muted)]">Invalid URL.</p>;
  }

  // Gate: Starter users cannot access YoY comparison
  if (!isPro()) {
    return (
      <UpsellSheet
        featureName={UPSELL_COPY.yoy_comparison.featureName}
        description={UPSELL_COPY.yoy_comparison.description}
        featureGate="yoy_comparison"
        onDismiss={() => navigate(-1)}
      />
    );
  }

  // Gate: Insufficient scouting history
  if (!hasMultiYearScoutingData(entries)) {
    return (
      <OperationalGuide
        illustration={noComparisonDataPng}
        heading="Not Enough History"
        subtext="Log scouting visits across two or more seasons to unlock year-over-year comparison"
        data-guide-id="comparison-insufficient-data"
      />
    );
  }

  function handlePeriodsChange(periodA: DateRange, periodB: DateRange) {
    setPeriodA(periodA);
    setPeriodB(periodB);
    void loadComparison({ waypointId: waypointId! });
  }

  const backUrl = propertyId && waypointId
    ? `/land/properties/${propertyId}/waypoints/${waypointId}`
    : '/land/properties';

  return (
    <div className="flex flex-col gap-6 animate-fade-in" data-testid="comparison-page">
      {/* Back + header */}
      <div className="flex flex-col gap-1">
        <Link
          to={backUrl}
          className="text-[var(--color-accent)] text-body-sm hover:text-[var(--color-accent-hover)] transition-colors"
        >
          ← {waypoint?.name ?? 'Waypoint'}
        </Link>
        <h1 className="font-display text-display-lg text-[var(--color-text-display)] mt-1">
          Scouting Comparison
          {waypoint && (
            <span className="text-[var(--color-text-secondary)] text-display-sm ml-2">
              — {waypoint.name}
            </span>
          )}
        </h1>
      </div>

      {/* Period picker */}
      <section className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4">
        <h2 className="field-label mb-3">
          Select Periods
        </h2>
        <PeriodPicker onPeriodsChange={handlePeriodsChange} />
      </section>

      {/* Loading */}
      {isLoading && (
        <p className="text-body-sm text-[var(--color-text-muted)]" data-testid="loading-state">
          Loading comparison…
        </p>
      )}

      {/* Error */}
      {error && !isLoading && (
        <p className="text-body-sm text-red-400" data-testid="error-state">
          {error}
        </p>
      )}

      {/* Results */}
      {result && !isLoading && (
        <>
          <ComparisonSummary periodA={result.periodA} periodB={result.periodB} />
          <ComparisonTimeline
            periodA={result.periodA}
            periodB={result.periodB}
            onEntryClick={(id) =>
              navigate(`/waypoints/${waypointId}/log/${id}`)
            }
          />
        </>
      )}

      {/* Prompt to pick periods if no result yet */}
      {!result && !isLoading && !error && (
        <p
          className="text-body-sm text-[var(--color-text-muted)]"
          data-testid="prompt-state"
        >
          Select periods above and tap Compare to see year-over-year data.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Property-level comparison
// ---------------------------------------------------------------------------

export function PropertyComparisonPage() {
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();

  const { properties, loadProperties } = usePropertyStore();
  const { entries, loadEntriesByProperty } = useLogEntryStore();
  const { result, isLoading, error, setPeriodA, setPeriodB, loadComparison, reset } =
    useComparisonStore();
  const isPro = useUserProfileStore((s) => s.isPro);

  useEffect(() => {
    if (properties.length === 0) void loadProperties();
  }, [properties.length, loadProperties]);

  useEffect(() => {
    if (propertyId) void loadEntriesByProperty(propertyId);
  }, [propertyId, loadEntriesByProperty]);

  useEffect(() => {
    return () => {
      reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const property = properties.find((p) => p.id === propertyId);

  if (!propertyId) {
    return <p className="text-body-sm text-[var(--color-text-muted)]">Invalid URL.</p>;
  }

  // Gate: Starter users cannot access YoY comparison
  if (!isPro()) {
    return (
      <UpsellSheet
        featureName={UPSELL_COPY.yoy_comparison.featureName}
        description={UPSELL_COPY.yoy_comparison.description}
        featureGate="yoy_comparison"
        onDismiss={() => navigate(-1)}
      />
    );
  }

  // Gate: Insufficient scouting history
  if (!hasMultiYearScoutingData(entries)) {
    return (
      <OperationalGuide
        illustration={noComparisonDataPng}
        heading="Not Enough History"
        subtext="Log scouting visits across two or more seasons to unlock year-over-year comparison"
        data-guide-id="comparison-insufficient-data"
      />
    );
  }

  function handlePeriodsChange(periodA: DateRange, periodB: DateRange) {
    setPeriodA(periodA);
    setPeriodB(periodB);
    void loadComparison({ propertyId: propertyId! });
  }

  return (
    <div className="flex flex-col gap-6 animate-fade-in" data-testid="comparison-page">
      {/* Back + header */}
      <div className="flex flex-col gap-1">
        <Link
          to={`/land/properties/${propertyId}`}
          className="text-[var(--color-accent)] text-body-sm hover:text-[var(--color-accent-hover)] transition-colors"
        >
          ← {property?.name ?? 'Property'}
        </Link>
        <h1 className="font-display text-display-lg text-[var(--color-text-display)] mt-1">
          Scouting Comparison
          {property && (
            <span className="text-[var(--color-text-secondary)] text-display-sm ml-2">
              — {property.name}
            </span>
          )}
        </h1>
      </div>

      {/* Period picker */}
      <section className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4">
        <h2 className="field-label mb-3">
          Select Periods
        </h2>
        <PeriodPicker onPeriodsChange={handlePeriodsChange} />
      </section>

      {isLoading && (
        <p className="text-body-sm text-[var(--color-text-muted)]" data-testid="loading-state">
          Loading comparison…
        </p>
      )}

      {error && !isLoading && (
        <p className="text-body-sm text-red-400" data-testid="error-state">
          {error}
        </p>
      )}

      {result && !isLoading && (
        <>
          <ComparisonSummary periodA={result.periodA} periodB={result.periodB} />
          <ComparisonTimeline
            periodA={result.periodA}
            periodB={result.periodB}
            onEntryClick={(id) => navigate(`/waypoints/${id}/log/${id}`)}
          />
        </>
      )}

      {!result && !isLoading && !error && (
        <p
          className="text-body-sm text-[var(--color-text-muted)]"
          data-testid="prompt-state"
        >
          Select periods above and tap Compare to see year-over-year data.
        </p>
      )}
    </div>
  );
}
